import React, { useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import classNames from 'classnames';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import { Paging } from '../../../global/_paging/paging-container';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import { IOrganizationDetailsDTO, TGetOrganizationsSortKeys } from '../../../../api/organizations.dao';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import DefaultButton from '../../../global/default-button/default-button';
import OptionsButton from '../../../global/options-button/options-button';
import { CommonModal } from '../../../global/modal/common/common-modal';
import { DefaultCloseModalButton } from '../../../global/modal/common/common-close-modal-buttons';
import UploadButton from '../shared/upload-button';
import { SearchInput } from '../../../global/search-input/search-input';
import { CSV_TEMPLATE_LINK } from '../../../../constants/navigation.constants';
import { AttendeeListViewModel } from '../_attendees-list/attendees-list.viewmodel';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { OrganizationsListItem } from './_organizations-list-item/organizations-list-item';
import { OrganizationsListViewModel } from './organizations-list.viewmodel';
import { LinkTargetValue } from '../../../../constants/common.constants';
import { EMPTY_SEARCH_RESULT, MORE_BUTTON_TEXT, TYPE } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: OrganizationsListViewModel;
    intl: IntlShape;
    attendeeListVM: AttendeeListViewModel;
    resetSearchQuery: () => void;
}

const OrganizationsListPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        intl,
        vm,
        resetSearchQuery,
        attendeeListVM
    } = props;

    useEffect(() => {
        vm.setAttendeeListVM(attendeeListVM);
    }, []);

    const theads: ITheadSort<TGetOrganizationsSortKeys>[] = [
        {
            title: intl.formatMessage({
                id: 'oe.common.organization',
                defaultMessage: 'Organization',
                description: 'Label text',
            }), sortField: 'name', width: '20%'
        },
        {
            title: intl.formatMessage(TYPE), sortField: 'organizationType', width: '15%'
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.ticker',
                defaultMessage: 'Ticker',
                description: 'Label text',
            }), sortField: 'organizationTicker', width: '15%'
        },

        {
            title: intl.formatMessage({
                id: 'oe.common.chaperone',
                defaultMessage: 'Chaperone',
                description: 'Label text',
            }),
            width: '15%'
        },
        { width: '5%' },
        {
            title: intl.formatMessage({
                id: 'oe.common.attendees',
                defaultMessage: 'Attendees',
                description: 'Label text',
            }), width: '40%'
        },
        {
            customNode:
                <div className={classNames('search-input-container', {
                    'no-result': (
                        !vm.getOrganizations()?.length &&
                        !!vm.searchQuery
                    )
                })}>
                    <SearchInput
                        resetSearch={resetSearchQuery}
                        onChange={(e, value) => vm.setSearchQuery(value)}
                        withResetSearchButton
                        withDebounce
                    />
                </div>, width: '145px'
        }
    ];

    const uploadOrganizationsButtonProperties = vm.composeUploadOrganizationsButtonProperties();
    const generateOrganizationsLinksButtonProperties = attendeeListVM.composeGenerateOrganizationsLinksButtonProperties();

    const options = [
        hasPermission(Permission.ATTENDEE_UPLOAD) && {
            label: uploadOrganizationsButtonProperties.text,
            disabled: uploadOrganizationsButtonProperties.disabled,
            action: () => vm.setUploadCSVModalOpen(true)
        },
        hasPermission(Permission.ATTENDEE_DOWNLOAD) && {
            label: intl.formatMessage({
                id: 'oe.attendees.downloadCSV',
                defaultMessage: 'Download Attendees CSV',
                description: 'Label text'
            }),
            action: vm.downloadOrganizationsCSV,
            disabled: vm.isOrganizationsListEmpty()
        },
        hasPermission(Permission.PRESENTER_LINK_DOWNLOAD) && {
            label: intl.formatMessage({
                id: 'oe.organization.downloadPresenterOrganizationLink',
                defaultMessage: 'Download Presenter Organization Links',
                description: 'Label text'
            }),
            action: vm.downloadOrganizationsLinksCSV,
            disabled: vm.isOrganizationsListEmpty()
        },
        hasPermission(Permission.BULK_GENERATE_ORGANIZATIONS_LINKS) && {
            label: generateOrganizationsLinksButtonProperties.text,
            action: attendeeListVM.generateOrganizationsLinks,
            disabled: generateOrganizationsLinksButtonProperties.disabled
        }
    ].filter(Boolean);

    const isEmpty = !vm.getOrganizations()?.length;
    const isLoading = vm.isFetchingOrganizations();
    const handleEditClick = (organizationId: number) => {
        vm.showUpdateOrganization(organizationId);
    };

    const isAttendeeEditable = hasPermission(Permission.UPDATE_ATTENDEE);
    const canCreateAttendee = hasPermission(Permission.CREATE_ATTENDEE);
    const canEditOrganization = hasPermission(Permission.UPDATE_ORGANIZATION);

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {hasPermission(Permission.CREATE_ORGANIZATION) && (<DefaultButton
                            modifier={['fat', 'confirm-safe']}
                            text={
                                <FormattedMessage
                                    description='Label text'
                                    defaultMessage='{prefix}New Organization'
                                    id='oe.organization.new'
                                    values={{
                                        prefix: '+ '
                                    }}
                                />
                            }
                            onClick={() => vm.showCreateOrganization()}
                        />)}
                        {!!options.length && <OptionsButton
                            modifier={['fat', 'options']}
                            label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                            options={options}
                        />}
                    </div>
                </div>
            </section>
            <section className='attendees-list fixed-list-layout'>
                <div>
                    <ListLayout
                        sortOptions={{
                            theads: theads,
                            sortRows: vm.setSortParams,
                            sortOrder: vm.getSortOrder(),
                            sortField: vm.getSortField(),
                        }}
                        isDisabled={isEmpty}
                    >
                        {(!vm.isFetchingOrganizations() && vm.getOrganizations())
                            ? vm.getOrganizations().map((item: IOrganizationDetailsDTO, index) => {
                                return <OrganizationsListItem
                                    organizer={item}
                                    isSelected={props.vm.isOrganizationEditWindowOpen(item.organizationId)}
                                    handleEditClick={handleEditClick}
                                    key={index}
                                    editAttendee={vm.editAttendee}
                                    onAttendeeHovered={vm.setOrganizationListItemWithAttendeesHovered}
                                    isAttendeesHovered={vm.isOrganizationListItemWithAttendeesHovered(item.organizationId)}
                                    searchQuery={vm.searchQuery}
                                    isAttendeeEditable={isAttendeeEditable}
                                    canCreateAttendee={canCreateAttendee}
                                    canEditOrganization={canEditOrganization}
                                    handleAddAttendeeClick={organizationId => vm.handleAddAttendee.call(vm, organizationId)}
                                />;
                            })
                            : <TableLoadingRow colSpan={theads.length} />
                        }
                    </ListLayout>
                    {isEmpty && !isLoading &&
                        <div className='empty-list-message'>
                            {!vm.searchQuery ?
                                <FormattedMessage
                                    description='Organizations list empty text'
                                    defaultMessage='No organizations have been added to this event.'
                                    id='oe.organizations.emptyListText'
                                />
                                :
                                <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                            }
                        </div>
                    }
                </div>
                {!isLoading && <Paging pagingVM={vm.getPagingVM()} />}

                <CommonModal
                    title={intl.formatMessage({
                        id: 'oe.attendees.upload',
                        defaultMessage: 'Upload Attendees',
                        description: 'Label text'
                    })}
                    isOpen={vm.getUploadCSVModalOpen()}
                    onRequestClose={() => {
                        vm.setUploadCSVModalOpen(false);
                    }}
                    footer={(
                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultButton
                                text={
                                    <FormattedMessage
                                        id='oe.attendee.uploadAttendees'
                                        description='Upload Attendees title'
                                        defaultMessage='Upload Attendees CSV'
                                    />
                                }
                                modifier={['fat', 'confirm-safe']}
                                onClick={() => vm.importOrganizationsCSV()}
                                disabled={!vm.getCSVFileName()}
                            />
                            <DefaultCloseModalButton />
                        </div>
                    )}>
                    <div className='attendees-list__csv-modal-form'>
                        <UploadButton
                            fileName={vm.getCSVFileName()}
                            onChange={vm.setOrganizationsCSVFile}
                            className='attendees-list-option attendees-list-option__in-modal'
                        />
                        <DefaultButton
                            text={intl.formatMessage({
                                id: 'oe.csv.downloadExampleTemplate',
                                defaultMessage: 'Download example CSV template',
                                description: 'Label text'
                            })}
                            modifier='link'
                            onClick={() => window.open(CSV_TEMPLATE_LINK, LinkTargetValue.BLANK)}
                        />
                    </div>
                </CommonModal>
            </section>
        </div>
    );
}));


const OrganizationsListContainer = (props: { vm: OrganizationsListViewModel; attendeeListVM: AttendeeListViewModel }) => {
    const { vm, attendeeListVM } = props;

    useEffect(() => {
        vm.setSearchQuery('');
        vm.fetchOrganizations();
        return () => {
            vm.clearOrganizations();
        };
    }, []);

    return <OrganizationsListPresentation
        vm={vm}
        attendeeListVM={attendeeListVM}
        resetSearchQuery={vm.clearSearchQuery}
    />;
};

export { OrganizationsListContainer as OrganizationsList, OrganizationsListPresentation };
